import * as React from 'react';
import {FunctionComponent, useState} from 'react';
import {MediaItem} from '../../../../../models/MediaItem';
import {v4} from 'uuid';
import {Media} from '../../../../../models/Media';
import {deviceRequirementsToString} from '../../../../../models/DeviceRequirementRule';
import {toHumanReadableLanguage} from '../../../../../models/Language';
import {classNames} from '../../../../../../../components/support/classnames';
import * as fuzzysort from 'fuzzysort';
import {DeviceName, toHumanReadableDeviceName} from '../../../../../models/DeviceName';

interface OwnProps {
    media: Media[];
    onMediaItem: (mediaItem: Omit<MediaItem, 'anchor' | 'playbackrate'>) => void;
    onCancel: () => void;
}

interface MediaFilter {
    query: string | null;
}

function devicesFromQuery(query: string | null): {devices: DeviceName[]; query: string | null} {
    if (query === null) {
        return {devices: [], query: null};
    }
    const deviceNameSearches = Object.values(DeviceName).map((deviceName) => {
        return {
            deviceName: deviceName,
            search: [deviceName, toHumanReadableDeviceName(deviceName)],
        };
    });

    const parts = query.split(' ');
    const devices: DeviceName[] = deviceNameSearches
        .filter((deviceNameSearch) => {
            return parts.some((part) => {
                return deviceNameSearch.search.some((search) => {
                    if (search.toLowerCase() === part.toLowerCase()) {
                        //Remove from parts
                        parts.splice(parts.indexOf(part), 1);
                        return true;
                    }
                    return false;
                });
            });
        })
        .map((deviceNameSearch) => deviceNameSearch.deviceName);

    const updatedQuery = parts.join(' ');
    return {devices: devices, query: updatedQuery === '' ? null : updatedQuery};
}

function searchUsingQuery(query: string | null, media: Media[]) {
    return query === null
        ? media
        : fuzzysort
              .go(query || '', media, {
                  keys: ['title'],
              })
              .map((result) => result.obj);
}

function filterForDevices(media: Media[], devices: DeviceName[]) {
    return media.filter((mediaItem) => {
        return devices.every((device) => {
            return mediaItem.device_requirements.some((deviceRequirement) => {
                return deviceRequirement.devices.some((deviceName) => deviceName === device);
            });
        });
    });
}

export const LinkMediaStepComponent: FunctionComponent<OwnProps> = ({media, onMediaItem, onCancel}) => {
    const [mediaId, setMediaId] = useState<number | null>(null);
    const [filter, setFilter] = useState<MediaFilter>({
        query: null,
    });

    const {query, devices} = devicesFromQuery(filter.query);

    const items = searchUsingQuery(query, filterForDevices(media, devices));

    return (
        <>
            <div className="modal-header">
                <h2>Choose media</h2>
            </div>
            <div className="modal-body">
                <div className="form-group">
                    <label htmlFor="media-filter-query">Search</label>
                    <input
                        id="media-filter-query"
                        type="text"
                        className="form-control"
                        value={filter.query || ''}
                        onChange={(e) => setFilter({...filter, query: e.target.value === '' ? null : e.target.value})}
                    />
                </div>

                <div className="add-media-item-wizard media-item-list">
                    <div className="media-item-list__content media-item-list__content--grid">
                        {items.map((item) => {
                            const language = item.language;
                            return (
                                <button
                                    key={item.id}
                                    onClick={() => setMediaId(item.id)}
                                    className={classNames('media-item', 'media-item--button', {
                                        'media-item--selected': item.id === mediaId,
                                    })}
                                >
                                    <div className="media-item__thumbnail">
                                        {item.thumbnail_url && <img src={item.thumbnail_url} loading="lazy" />}
                                    </div>
                                    <div className="media-item__title">
                                        <strong>{item.title}</strong>
                                        <br />
                                        Equipment needed: {deviceRequirementsToString(item.device_requirements)}
                                        <br />
                                        {language === null ? null : ' in ' + toHumanReadableLanguage(language)}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
            <div className="modal-footer">
                <button
                    className="btn btn-primary pull-right"
                    disabled={mediaId === null}
                    onClick={() =>
                        mediaId !== null &&
                        onMediaItem({
                            __uuid: v4(),
                            mediaId: mediaId,
                        })
                    }
                >
                    Next
                </button>
                <button className="btn btn-secondary pull-right" onClick={onCancel}>
                    Cancel
                </button>
            </div>
        </>
    );
};
